import { useEffect, useState } from 'react'
import { fbt } from 'fbtee'
import { Heart } from 'lucide-react'
import { cn } from '../lib/utils'
import { hyphenateFirstWord, hyphenatesName } from '../lib/hyphenate'
import { cardNameClass, cardNameSize, cardNameStyle, isOneLineName } from '../lib/cardName'
import { CardBackground } from './CardBackground'
import type { FocalAnchor } from '../lib/focalPosition'
import { MemberFacepile } from './MemberFacepile'
import { GoingCheckIcon } from './GoingCheckIcon'
import { UpdatedChip } from './UpdatedChip'
import type { InterestStatus } from '../types'

type CardStatus = 'none' | InterestStatus
export type Compact = 'xs' | 'sm' | 'md' | undefined
export type ActiveWindow = { top: number; height: number }

const dimmedClass = 'opacity-40 pointer-events-none transition-opacity duration-200'

export function CardBackdrop({
  activeWindow,
  layered,
  imageUrl,
  imageFocal,
  imageUrls,
  imageFocals,
  seed,
  status,
  imgOverlay,
  noImgOverlay,
}: {
  activeWindow?: ActiveWindow
  layered: boolean
  imageUrl?: string
  imageFocal?: FocalAnchor
  imageUrls?: string[]
  imageFocals?: FocalAnchor[]
  seed?: string
  status: CardStatus
  imgOverlay: number
  noImgOverlay: number
}) {
  if (!activeWindow) {
    if (!layered) return null
    return (
      <CardBackground
        imageUrl={imageUrl}
        imageFocal={imageFocal}
        imageUrls={imageUrls}
        imageFocals={imageFocals}
        seed={seed}
        imageOverlay={imgOverlay}
        gradientOverlay={noImgOverlay}
      />
    )
  }
  return (
    <>
      <CardBackground
        imageUrl={imageUrl}
        imageFocal={imageFocal}
        imageUrls={imageUrls}
        imageFocals={imageFocals}
        seed={seed}
        imageOverlay={0.6}
        gradientOverlay={0.75}
        className="grayscale opacity-60"
      />
      <div
        data-testid="partial-set-window"
        className={cn(
          'absolute inset-x-0 z-0 rounded border-2',
          status === 'going' ? 'border-going' : 'border-interested',
        )}
        style={{ top: `${activeWindow.top * 100}%`, height: `${activeWindow.height * 100}%` }}
      >
        <CardBackground
          imageUrl={imageUrl}
          imageFocal={imageFocal}
          imageUrls={imageUrls}
          imageFocals={imageFocals}
          seed={seed}
          imageOverlay={imgOverlay}
          gradientOverlay={noImgOverlay}
          frame={{ top: -activeWindow.top / activeWindow.height, height: 1 / activeWindow.height }}
        />
      </div>
    </>
  )
}

export function CardHeader({
  compact,
  nameLines,
  dimContent,
  heartRef,
  status,
  hasBackground,
  onToggle,
  nameContent,
  name,
  tagline,
  subtitle,
  updated = false,
  followHint = false,
}: {
  compact: Compact
  nameLines?: number
  dimContent: boolean
  heartRef?: React.Ref<HTMLElement>
  status: CardStatus
  hasBackground: boolean
  onToggle: () => void
  nameContent?: React.ReactNode
  name?: string
  tagline?: string
  subtitle?: string
  updated?: boolean
  followHint?: boolean
}) {
  const oneLine = isOneLineName(compact, nameLines)
  // A one-line sm block has no room left for the time; md always keeps a line for it.
  const showSubtitle = compact !== 'xs' && !(compact === 'sm' && oneLine)
  // Smaller on sm blocks: a size-5 heart makes a one-line name's row too tall
  // for the time to fit under it on a 25-minute block.
  const heartSize = compact === 'sm' ? 'size-4' : 'size-5'
  const [pillEl, setPillEl] = useState<HTMLDivElement | null>(null)
  const pillFits = useFitsInParent(pillEl)
  // The Updated pill takes a line under the time; a card with no room for it
  // (or no time line) gets a dot beside the heart instead (after the name on
  // xs), which never costs the name a line.
  const pill = updated && compact !== 'xs' && showSubtitle && !!subtitle
  const dot = updated && (!pill || !pillFits) && <UpdatedChip variant="dot" className="self-center" />
  const heart = compact !== 'xs' && (
    // Floated, it's one name line tall, so only the first line wraps around it
    // — a word that didn't fit beside it on the second line used to drop a
    // whole heart's height lower. The icon is taller than a text-xs line, so on
    // narrow cards it rides up into the header's top padding. An sm block has
    // no padding to spare, so its heart keeps the plain float.
    <div
      className={cn(
        'relative z-10 flex gap-0.5',
        oneLine ? 'flex-none items-center -my-1 -mr-1' : compact === 'sm' ? 'float-right items-center -m-1' : cn('float-right items-start h-[1lh] leading-tight -mr-1 -mt-2 @[128px]:-mt-1', cardNameSize(compact)),
        dimContent && dimmedClass,
      )}
    >
      {dot}
      <button
        ref={heartRef as React.Ref<HTMLButtonElement> | undefined}
        type="button"
        aria-label={
          status === 'interested' || status === 'going'
            ? fbt('Remove interest', 'Accessible label for the heart button that removes interest on an interest card')
            : fbt('Mark interested', 'Accessible label for the heart button that marks interest on an interest card')
        }
        onClick={e => {
          e.stopPropagation()
          onToggle()
        }}
        className="cursor-pointer p-1"
      >
        {status === 'going' ? (
          <GoingCheckIcon className={heartSize} />
        ) : (
          <Heart
            // An artist followed app-wide but not hearted here: an outline only, never the filled mark.
            data-testid={followHint && status === 'none' ? 'followed-artist-hint' : undefined}
            className={cn(
              heartSize,
              status === 'interested'
                ? 'fill-interested text-interested'
                : followHint
                  ? 'text-interested'
                  : hasBackground
                  ? 'text-on-image/60'
                  : 'text-muted-foreground',
            )}
          />
        )}
      </button>
    </div>
  )
  const nameEl = nameContent ?? (
    <p
      className={cn('font-semibold leading-tight', cardNameClass(compact, nameLines), hasBackground ? 'text-on-image' : '')}
      style={cardNameStyle(compact, nameLines)}
    >
      {name && hyphenatesName(compact, nameLines) ? hyphenateFirstWord(name) : name}
    </p>
  )

  if (compact === 'xs') {
    return (
      <div className={cn('relative z-30 flex-1 overflow-hidden flex items-center', dimContent && 'pointer-events-none')}>
        {nameEl}
        {dot && <span className="ml-1 flex">{dot}</span>}
      </div>
    )
  }
  // A wrapping column: a line that doesn't fit whole under the name (the time
  // on a short block whose name took every line) wraps into a second column
  // the overflow hides, instead of showing cut in half. `clip`, not `hidden`:
  // the heart pokes past the right edge (-mr-1), and focusing it on tap
  // scrolled a `hidden` box sideways, cutting off the name's first letters.
  return (
    <div className={cn('relative z-30 flex-1 overflow-clip flex flex-col flex-wrap content-start', compact !== 'sm' && 'pt-1', dimContent && 'pointer-events-none')}>
      <div className={cn('w-full', oneLine && 'flex items-center gap-1')}>
        {oneLine ? <>{nameEl}{heart}</> : <>{heart}{nameEl}</>}
      </div>
      {!compact && tagline && (
        <p
          className={cn(
            'w-full text-[10px] @[128px]:text-xs @[188px]:text-sm leading-tight truncate mt-0.5 select-none',
            hasBackground ? 'text-on-image/80' : 'text-muted-foreground',
          )}
        >
          {tagline}
        </p>
      )}
      {showSubtitle && subtitle && (
        <p
          className={cn(
            'w-full text-[10px] @[128px]:text-xs @[188px]:text-sm tabular-nums leading-none mt-0.5',
            hasBackground ? 'text-on-image/60' : 'text-muted-foreground',
          )}
        >
          {subtitle}
        </p>
      )}
      {pill && (
        <div ref={setPillEl} className={cn('w-full flex mt-1', !pillFits && 'invisible')}>
          <UpdatedChip ghost={!pillFits} />
        </div>
      )}
    </div>
  )
}

// Whether `el` shows whole inside its parent. CardHeader's wrapping column
// moves a line that doesn't fit into a second column its overflow clips — which
// can happen after mount too (a web font loading, the card resizing), so it's
// observed rather than measured once.
function useFitsInParent(el: HTMLElement | null): boolean {
  const [fits, setFits] = useState(true)
  useEffect(() => {
    const parent = el?.parentElement
    if (!el || !parent) return
    const observer = new IntersectionObserver(([entry]) => setFits(entry.intersectionRatio > 0.99), {
      root: parent,
      threshold: [0.99, 1],
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [el])
  return fits
}

export function CardFooter({
  dimContent,
  badge,
  ...facepile
}: { dimContent: boolean; badge?: React.ReactNode } & Omit<React.ComponentProps<typeof MemberFacepile>, 'responsive'>) {
  return (
    <div className={cn('relative z-30 flex items-center gap-1', dimContent && dimmedClass)}>
      <MemberFacepile {...facepile} responsive />
      {badge && <div className="ml-auto shrink-0">{badge}</div>}
    </div>
  )
}
